/**
 * Sector operations — Connector Framework Data and Edge Reducer.
 * R39 M11.a (SO-D231, SO-P275).
 */

export type OpsConnectorKind =
  | 'GENERIC_WEBHOOK'
  | 'CSV'
  | 'CRELIO'
  | 'MOCDOC'
  | 'MARG'
  | 'EVITALRX'

export const SUPPORTED_CONNECTOR_KINDS: readonly OpsConnectorKind[] = [
  'GENERIC_WEBHOOK',
  'CSV',
] as const

export const RESERVED_NAMED_CONNECTOR_KINDS: readonly OpsConnectorKind[] = [
  'CRELIO',
  'MOCDOC',
  'MARG',
  'EVITALRX',
] as const

export const ALL_CONNECTOR_KINDS: readonly OpsConnectorKind[] = [
  'GENERIC_WEBHOOK',
  'CSV',
  'CRELIO',
  'MOCDOC',
  'MARG',
  'EVITALRX',
] as const

export type OpsConnectorState = 'ACTIVE' | 'PAUSED'

export interface OpsConnector {
  id: string
  workspaceId: string
  sector: string
  kind: OpsConnectorKind
  name: string
  state: OpsConnectorState
  eventMap: Record<string, string>
  secretId: string
  lastEventAt: number | null
  errorCount: number
  createdBy: string
  createdAt: unknown
  updatedBy: string
  updatedAt: unknown
}

export type ConnectorEventRefKind = 'BARCODE' | 'TOKEN'

export const CONNECTOR_REF_KINDS: readonly ConnectorEventRefKind[] = [
  'BARCODE',
  'TOKEN',
] as const

export const CONNECTOR_BARCODE_STATUSES = [
  'RECEIVED_AT_LAB',
  'REJECTED',
  'REPORT_READY',
] as const

export const CONNECTOR_TOKEN_STATUSES = [
  'CALLED',
  'SERVED',
  'SKIPPED',
] as const

export const ALL_CONNECTOR_STATUSES = [
  'RECEIVED_AT_LAB',
  'REJECTED',
  'REPORT_READY',
  'CALLED',
  'SERVED',
  'SKIPPED',
] as const

export type ConnectorTargetStatus = (typeof ALL_CONNECTOR_STATUSES)[number]

export type ConnectorEventOutcome = 'APPLIED' | 'IGNORED' | 'ERROR'

export type ConnectorErrorSource = 'INPUT' | 'APPLY'

export interface ReducedConnectorEvent {
  eventId: string
  refKind: ConnectorEventRefKind
  ref: string
  status: string
  occurredAt: number
  rejectionReason?: string
}

export interface OpsConnectorEvent {
  id: string
  connectorId: string
  workspaceId: string
  eventId: string
  refKind?: ConnectorEventRefKind
  ref?: string
  status?: string
  occurredAt: number
  receivedAt: number
  outcome: ConnectorEventOutcome
  errorSource?: ConnectorErrorSource
  reason?: string
  expiresAt: number
  replayedAt?: number
  replayedBy?: string
}

export function isSupportedConnectorKind(kind: unknown): kind is 'GENERIC_WEBHOOK' | 'CSV' {
  return typeof kind === 'string' && (SUPPORTED_CONNECTOR_KINDS as readonly string[]).includes(kind)
}

export function isReservedNamedConnectorKind(
  kind: unknown,
): kind is 'CRELIO' | 'MOCDOC' | 'MARG' | 'EVITALRX' {
  return typeof kind === 'string' && (RESERVED_NAMED_CONNECTOR_KINDS as readonly string[]).includes(kind)
}

/**
 * Asserts that the connector kind is ready to be used.
 * Throws failed-precondition CONNECTOR_KIND_NOT_READY for reserved named kinds.
 */
export function assertConnectorKindReady(kind: unknown): void {
  if (isReservedNamedConnectorKind(kind)) {
    const error = Object.assign(
      new Error(
        `Connector kind '${kind}' is not ready yet (CONNECTOR_KIND_NOT_READY). Reserved for subsequent connector phases.`,
      ),
      {
        code: 'failed-precondition',
        details: { code: 'CONNECTOR_KIND_NOT_READY', kind },
      },
    )
    throw error
  }
  if (!isSupportedConnectorKind(kind)) {
    const error = Object.assign(new Error(`Unsupported connector kind: '${kind}'`), {
      code: 'invalid-argument',
    })
    throw error
  }
}

/**
 * Validates and normalizes an event mapping dictionary.
 * Each entry maps a vendor status word to one of the allowed statuses.
 */
export function validateEventMap(eventMap: unknown): Record<string, string> {
  if (!eventMap || typeof eventMap !== 'object' || Array.isArray(eventMap)) {
    return {}
  }
  const result: Record<string, string> = {}
  for (const [key, val] of Object.entries(eventMap as Record<string, unknown>)) {
    const trimmedKey = String(key).trim()
    const targetStatus = String(val).trim()
    if (!trimmedKey) continue
    if (!(ALL_CONNECTOR_STATUSES as readonly string[]).includes(targetStatus)) {
      throw new Error(`Invalid target status '${targetStatus}' for vendor status '${trimmedKey}'. Must be one of ${ALL_CONNECTOR_STATUSES.join(', ')}`)
    }
    result[trimmedKey] = targetStatus
  }
  return result
}

/**
 * Edge reducer: drops all extraneous fields (names, phones, test results, doctor notes, etc.)
 * and keeps only the normalized operational fields: eventId, refKind, ref, status, occurredAt, rejectionReason.
 */
export function reduceConnectorEvent(
  raw: unknown,
  eventMap?: Record<string, string>,
): { ok: true; event: ReducedConnectorEvent } | { ok: false; reason: string } {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ok: false, reason: 'INVALID_PAYLOAD' }
  }

  const obj = raw as Record<string, unknown>

  // 1. refKind check
  const rawRefKind = typeof obj.refKind === 'string' ? obj.refKind.trim().toUpperCase() : ''
  if (!rawRefKind || !(CONNECTOR_REF_KINDS as readonly string[]).includes(rawRefKind)) {
    return { ok: false, reason: 'INVALID_REF_KIND' }
  }
  const refKind = rawRefKind as ConnectorEventRefKind

  // 2. ref check
  const ref = typeof obj.ref === 'string' ? obj.ref.trim() : typeof obj.barcode === 'string' ? obj.barcode.trim() : typeof obj.token === 'string' ? obj.token.trim() : ''
  if (!ref) {
    return { ok: false, reason: 'MISSING_REF' }
  }

  // 3. Status mapping
  const rawStatus = typeof obj.status === 'string' ? obj.status.trim() : ''
  let mappedStatus = ''
  if (eventMap && typeof eventMap === 'object') {
    if (eventMap[rawStatus]) {
      mappedStatus = eventMap[rawStatus]
    } else if (eventMap[rawStatus.toLowerCase()]) {
      mappedStatus = eventMap[rawStatus.toLowerCase()]
    }
  }
  if (!mappedStatus) {
    if ((ALL_CONNECTOR_STATUSES as readonly string[]).includes(rawStatus.toUpperCase())) {
      mappedStatus = rawStatus.toUpperCase()
    }
  }

  if (!mappedStatus || !(ALL_CONNECTOR_STATUSES as readonly string[]).includes(mappedStatus)) {
    return { ok: false, reason: 'UNMAPPED_STATUS' }
  }

  // Status must be compatible with refKind
  if (refKind === 'BARCODE' && !(CONNECTOR_BARCODE_STATUSES as readonly string[]).includes(mappedStatus)) {
    return { ok: false, reason: 'STATUS_REFKIND_MISMATCH' }
  }
  if (refKind === 'TOKEN' && !(CONNECTOR_TOKEN_STATUSES as readonly string[]).includes(mappedStatus)) {
    return { ok: false, reason: 'STATUS_REFKIND_MISMATCH' }
  }

  // 4. eventId
  let eventId = typeof obj.eventId === 'string' ? obj.eventId.trim() : typeof obj.id === 'string' ? obj.id.trim() : ''
  if (!eventId) {
    // Generate deterministic id if not supplied
    const timeForId = typeof obj.occurredAt === 'number' ? obj.occurredAt : Date.now()
    eventId = `${refKind}_${ref}_${mappedStatus}_${timeForId}`
  }

  // 5. occurredAt
  let occurredAt = typeof obj.occurredAt === 'number' ? obj.occurredAt : typeof obj.timestamp === 'number' ? obj.timestamp : Date.now()
  if (typeof obj.occurredAt === 'string') {
    const parsed = Date.parse(obj.occurredAt)
    if (!Number.isNaN(parsed)) occurredAt = parsed
  }

  const result: ReducedConnectorEvent = {
    eventId,
    refKind,
    ref,
    status: mappedStatus,
    occurredAt,
  }

  if (mappedStatus === 'REJECTED') {
    const rawReason = typeof obj.rejectionReason === 'string' ? obj.rejectionReason.trim() : typeof obj.reason === 'string' ? obj.reason.trim() : ''
    if (rawReason) {
      result.rejectionReason = rawReason
    }
  }

  return { ok: true, event: result }
}
